/**
 * Image cropping utility for face search
 * Crops images based on normalized face bounding boxes (0-1 coordinates)
 */

import fileUtils from '@/domain/core/utils/fileUtils'

/**
 * Crop image to a specific face bounding box
 * @param {File|Blob} imageFile - Original image file
 * @param {Object} faceBbox - Face bounding box {left, top, width, height} in 0-1 normalized coordinates
 * @param {Number} paddingRatio - Padding around face as fraction of bbox size (default 0.2 = 20%)
 * @returns {Promise<Blob>} Cropped image as JPEG blob
 */
export async function cropImageToFace(imageFile, faceBbox, paddingRatio = 0.2) {
    return new Promise((resolve, reject) => {
        const img = new Image()
        const canvas = document.createElement('canvas')
        const ctx = canvas.getContext('2d')

        img.onload = () => {
            const imgWidth = img.naturalWidth
            const imgHeight = img.naturalHeight

            // Calculate coordinates with padding
            const paddedLeft = Math.max(0, faceBbox.left - paddingRatio * faceBbox.width)
            const paddedTop = Math.max(0, faceBbox.top - paddingRatio * faceBbox.height)
            const paddedWidth = Math.min(1.0 - paddedLeft, faceBbox.width * (1 + 2 * paddingRatio))
            const paddedHeight = Math.min(1.0 - paddedTop, faceBbox.height * (1 + 2 * paddingRatio))

            // Convert normalized coordinates (0-1) to pixel coordinates
            const cropX = paddedLeft * imgWidth
            const cropY = paddedTop * imgHeight
            const cropWidth = paddedWidth * imgWidth
            const cropHeight = paddedHeight * imgHeight

            // Set canvas size to crop dimensions
            canvas.width = cropWidth
            canvas.height = cropHeight

            // Draw cropped portion of image
            ctx.drawImage(
                img,
                cropX, cropY, cropWidth, cropHeight,  // Source rectangle
                0, 0, cropWidth, cropHeight            // Destination rectangle
            )

            // Convert canvas to JPEG blob (high quality to preserve face details)
            canvas.toBlob(blob => {
                if (blob) {
                    resolve(blob)
                } else {
                    reject(new Error('Failed to crop image'))
                }
            }, 'image/jpeg', 0.95)
        }

        img.onerror = () => {
            reject(new Error('Failed to load image'))
        }

        // Load image from file
        img.src = URL.createObjectURL(imageFile)
    })
}

/**
 * Validate image file before processing
 * @param {File} file - Image file to validate
 * @param {Number} maxSizeMB - Maximum file size in MB (default 10)
 * @returns {Object} {valid: boolean, error: string|null}
 */
export function validateImageFile(file, maxSizeMB = 10) {
    // Check if file exists
    if (!file) {
        return { valid: false, error: 'No file provided' }
    }

    // Check file type (JPEG only)
    const validTypes = ['image/jpeg', 'image/jpg']
    if (!validTypes.includes(file.type)) {
        return {
            valid: false,
            error: 'Invalid image format. Please use JPEG only.'
        }
    }

    // Check file size
    const maxSizeBytes = maxSizeMB * 1024 * 1024
    if (file.size > maxSizeBytes) {
        return {
            valid: false,
            error: `File too large. Maximum size is ${maxSizeMB}MB.`
        }
    }

    return { valid: true, error: null }
}

/**
 * Compress image to target size for face search API
 * @param {File} file - Image file to compress
 * @param {Number} maxSizeBytes - Maximum file size in bytes (default 1MB)
 * @returns {Promise<File|Blob>} Compressed image
 */
export async function compressImageForSearch(file, maxSizeBytes = 1024 * 1024) {
    // If already small enough, return as-is
    if (file.size <= maxSizeBytes) {
        return file
    }

    // Calculate quality based on how much we need to reduce
    // Start with 0.8 quality and adjust based on file size ratio
    const ratio = maxSizeBytes / file.size
    const quality = Math.max(0.5, Math.min(0.8, ratio + 0.1))

    // Calculate max dimensions to help reduce file size
    // For face search, we don't need huge images - 1500px max dimension should be enough
    const maxDimension = 1500

    try {
        const compressedBlob = await fileUtils.compress(file, {
            quality,
            maxWidth: maxDimension,
            maxHeight: maxDimension,
            mimeType: 'image/jpeg',
            convertSize: maxSizeBytes
        })

        // Convert Blob back to File to preserve the filename
        const fileName = file.name || 'image.jpg'
        return new File([compressedBlob], fileName, { type: 'image/jpeg' })
    } catch (error) {
        console.warn('Image compression failed, using original:', error)
        return file
    }
}

export default {
    cropImageToFace,
    validateImageFile,
    compressImageForSearch
}
